import { useEffect, useState } from "react";
import qrcode from "qrcode-generator";
import { ApiError, type TwoFactorStatus, api } from "./api";
import { Alert, Badge, Box, Working } from "./ui";

/**
 * Two-step sign in, set up by the person it belongs to (Daren's question, 23 September).
 *
 * A password is one thing somebody can watch you type at a trade show. This asks for a
 * second thing, from a phone: scan the square with an authenticator app, type one code
 * back to prove the app works, and keep the backup codes for the day the phone is lost.
 *
 * It is per login and off until it is turned on. A system that locks the owner out of his
 * own till on the morning of a show is worse than the risk it was guarding against.
 */
export function TwoFactor(): React.ReactElement {
  const [status, setStatus] = useState<TwoFactorStatus | undefined>();
  const [step, setStep] = useState<"idle" | "password" | "scan" | "codes" | "off">("idle");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<{ secret: string; url: string } | undefined>();
  const [backupCodes, setBackupCodes] = useState<string[] | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function reload(): Promise<void> {
    const result = await api.twoFactor();
    setStatus(result.twoFactor);
  }

  useEffect(() => {
    void reload().catch(() => setProblem("Could not load the two-step verification status"));
  }, []);

  function stop(): void {
    setStep("idle");
    setPassword("");
    setCode("");
    setSetup(undefined);
    setProblem(undefined);
  }

  async function run(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      await work();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "The request could not be completed");
    } finally {
      setBusy(false);
    }
  }

  if (!status) return <Box title="Two-step verification">{null}</Box>;

  return (
    <Box title="Two-step verification">
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      {notice ? (
        <Alert tone="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      ) : null}

      {step === "idle" ? (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[16rem]">
            <p className="mb-1 flex items-center gap-2">
              {status.on ? <Badge tone="success">Enabled</Badge> : <Badge tone="light">Disabled</Badge>}
              <span className="font-semibold">
                {status.on ? "Password and authenticator app code" : "Password only"}
              </span>
            </p>
            <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-0">
              {status.on
                ? `Enabled since ${status.since?.slice(0, 10) ?? "recently"}. ${status.backupCodesLeft} backup ${status.backupCodesLeft === 1 ? "code" : "codes"} remaining.`
                : "A password alone can be observed as you type it. Set up an authenticator app for a second verification step."}
            </p>
          </div>
          {status.on ? (
            <div className="flex gap-2">
              <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setStep("codes")}>
                <i className="ri-key-2-line" /> Generate new backup codes
              </button>
              <button type="button" className="ti-btn ti-btn-light !mb-0 !text-danger" onClick={() => setStep("off")}>
                Turn off
              </button>
            </div>
          ) : (
            <button type="button" className="ti-btn ti-btn-primary-full !mb-0" onClick={() => setStep("password")}>
              <i className="ri-shield-keyhole-line" /> Set up an authenticator app
            </button>
          )}
        </div>
      ) : null}

      {step === "password" ? (
        <div className="max-w-[26rem]">
          <p className="text-[#8c9097] dark:text-white/50">
            Confirm your password first, so nobody with access to an unattended screen can link their own phone to your
            account.
          </p>
          <label htmlFor="twofactor-password" className="form-label">
            Current password
          </label>
          <input
            id="twofactor-password"
            type="password"
            autoComplete="current-password"
            className="form-control"
            value={password}
            onChange={(changed) => setPassword(changed.target.value)}
          />
          <div className="flex gap-2 mt-4">
            <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={stop}>
              Cancel
            </button>
            <button
              type="button"
              className="ti-btn ti-btn-primary-full !mb-0"
              disabled={busy || password === ""}
              onClick={() =>
                void run(async () => {
                  setSetup(await api.startTwoFactor(password));
                  setStep("scan");
                })
              }
            >
              {busy ? <Working label="Checking" /> : "Next"}
            </button>
          </div>
        </div>
      ) : null}

      {step === "scan" && setup ? (
        <div className="grid grid-cols-12 gap-6">
          <div className="lg:col-span-5 col-span-12">
            <p className="font-semibold mb-2">1. Scan this code with your authenticator app</p>
            <Qr text={setup.url} />
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-3 mb-1">
              For example Google Authenticator, 1Password or Authy. If you cannot scan it, enter this key instead:
            </p>
            <code className="text-[0.813rem] tracking-[0.15em] break-all">{setup.secret}</code>
          </div>
          <div className="lg:col-span-7 col-span-12">
            <p className="font-semibold mb-2">2. Enter the six-digit code it shows</p>
            <input
              id="twofactor-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="form-control !text-[1.25rem] !tracking-[0.3em] text-center max-w-[12rem]"
              placeholder="000000"
              value={code}
              onChange={(changed) => setCode(changed.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            <div className="flex gap-2 mt-4">
              <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={stop}>
                Cancel
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-primary-full !mb-0"
                disabled={busy || code.length !== 6}
                onClick={() =>
                  void run(async () => {
                    const result = await api.confirmTwoFactor(code);
                    setBackupCodes(result.backupCodes);
                    setCode("");
                    setPassword("");
                    setSetup(undefined);
                    setStep("idle");
                    setNotice("Two-step verification is on. Keep the backup codes somewhere safe.");
                    await reload();
                  })
                }
              >
                {busy ? <Working label="Checking" /> : "Turn on"}
              </button>
            </div>
            <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
              From then on, sign in with your password, then the code. The code changes every 30 seconds.
            </p>
          </div>
        </div>
      ) : null}

      {step === "codes" ? (
        <div className="max-w-[26rem]">
          <p className="text-[#8c9097] dark:text-white/50">
            Generating new backup codes replaces the existing ones. Enter your password to continue.
          </p>
          <label htmlFor="twofactor-again" className="form-label">
            Current password
          </label>
          <input
            id="twofactor-again"
            type="password"
            autoComplete="current-password"
            className="form-control"
            value={password}
            onChange={(changed) => setPassword(changed.target.value)}
          />
          <div className="flex gap-2 mt-4">
            <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={stop}>
              Cancel
            </button>
            <button
              type="button"
              className="ti-btn ti-btn-primary-full !mb-0"
              disabled={busy || password === ""}
              onClick={() =>
                void run(async () => {
                  const result = await api.newBackupCodes(password);
                  setBackupCodes(result.backupCodes);
                  setPassword("");
                  setStep("idle");
                  setNotice("Ten new backup codes generated. The previous codes no longer work.");
                  await reload();
                })
              }
            >
              {busy ? <Working label="Generating" /> : "Generate codes"}
            </button>
          </div>
        </div>
      ) : null}

      {step === "off" ? (
        <div className="max-w-[26rem]">
          <p className="text-[#8c9097] dark:text-white/50">
            Turning this off leaves your password as the only protection for this account. Enter your password and a
            current code to continue.
          </p>
          <label htmlFor="twofactor-off-password" className="form-label">
            Current password
          </label>
          <input
            id="twofactor-off-password"
            type="password"
            autoComplete="current-password"
            className="form-control"
            value={password}
            onChange={(changed) => setPassword(changed.target.value)}
          />
          <label htmlFor="twofactor-off-code" className="form-label mt-3">
            Authenticator app code or backup code
          </label>
          <input
            id="twofactor-off-code"
            type="text"
            className="form-control"
            value={code}
            onChange={(changed) => setCode(changed.target.value.toUpperCase())}
          />
          <div className="flex gap-2 mt-4">
            <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={stop}>
              Cancel
            </button>
            <button
              type="button"
              className="ti-btn ti-btn-danger-full !mb-0"
              disabled={busy || password === "" || code.trim().length < 6}
              onClick={() =>
                void run(async () => {
                  await api.stopTwoFactor(password, code.trim());
                  setPassword("");
                  setCode("");
                  setStep("idle");
                  setBackupCodes(undefined);
                  setNotice("Two-step verification is off.");
                  await reload();
                })
              }
            >
              {busy ? <Working label="Turning off" /> : "Turn off"}
            </button>
          </div>
        </div>
      ) : null}

      {backupCodes ? (
        <div className="mt-6 border-t border-defaultborder dark:border-defaultborder/10 pt-4">
          <p className="font-semibold mb-1">Your backup codes</p>
          <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
            Each code works once, in place of the authenticator app code. They are shown only now: print them or store
            them somewhere secure.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 my-3">
            {backupCodes.map((backup) => (
              <code key={backup} className="text-center py-2 rounded-sm bg-light dark:bg-black/20 tracking-[0.1em]">
                {backup}
              </code>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="ti-btn ti-btn-light !mb-0"
              onClick={() => void navigator.clipboard?.writeText(backupCodes.join("\n"))}
            >
              <i className="ri-file-copy-line" /> Copy codes
            </button>
            <button type="button" className="ti-btn ti-btn-light !mb-0" onClick={() => setBackupCodes(undefined)}>
              I have saved them
            </button>
          </div>
        </div>
      ) : null}
    </Box>
  );
}

/**
 * The square an authenticator app scans.
 *
 * Drawn as SVG rather than a picture, so it stays sharp on any screen and needs no canvas.
 * The dark squares take the page's own text colour, which keeps it readable in both
 * themes; a scanner only cares that dark and light are far enough apart.
 */
function Qr({ text }: { readonly text: string }): React.ReactElement {
  const model = qrcode(0, "M");
  model.addData(text);
  model.make();

  const count = model.getModuleCount();
  // The quiet border the standard asks for, without which some scanners will not look.
  const margin = 4;
  const size = count + margin * 2;
  const squares: string[] = [];
  for (let row = 0; row < count; row += 1) {
    for (let column = 0; column < count; column += 1) {
      if (model.isDark(row, column)) squares.push(`M${column + margin} ${row + margin}h1v1h-1z`);
    }
  }

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="The square to scan with your authenticator app"
      className="w-[13rem] h-[13rem] bg-white rounded-sm p-1"
      shapeRendering="crispEdges"
    >
      <path d={squares.join("")} fill="#111" />
    </svg>
  );
}
